// src/utils/request.js
import axios from 'axios';
import { Message } from 'element-ui';
import CookieUtil from './CookieUtil';
import router from '@/router';

// 创建默认axios实例（不设置baseURL）
const service = axios.create({
    timeout: 120000 // 请求超时时间
});

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 从 Cookies 获取 token
        const token = CookieUtil.get('token');
        if (token) {
            config.headers['Token'] = `${token}`;
        }
        if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    error => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    response => {
        // 检查是否是文件下载（根据content-type或自定义标记）
        const isFileDownload = response.config.responseType === 'blob' ||
            (response.headers &&
                Object.keys(response.headers).some(key =>
                    key.toLowerCase() === 'content-type' &&
                    response.headers[key].includes('application/octet-stream')
                ));

        // 如果是文件下载，返回完整响应
        if (isFileDownload) {
            return response;
        }

        // 否则返回数据部分
        return response.data;
    },
    error => {
        // 如果有响应，说明服务器返回了错误状态码
        if (error.response) {
            const { status, data } = error.response;

            switch (status) {
                case 400:
                    Message.error(data.message || '请求参数错误');
                    break;
                case 401:
                    Message.error('登录已过期，请重新登录');
                    // 跳转到登录页
                    router.push('/login');
                    break;
                case 403:
                    Message.error('没有权限访问该资源');
                    break;
                case 404:
                    Message.error('请求资源不存在');
                    break;
                case 500:
                    Message.error(data.message || '服务器内部错误');
                    break;
                default:
                    Message.error(`请求错误: ${status}`);
            }
        } else {
            // 处理超时错误
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                Message.error('请求超时，请检查网络连接后重试');
                // 返回一个空对象或其他默认值，避免后续的catch处理
                return Promise.resolve({});
            } else if (error.message === 'Network Error') {
                Message.error('网络连接错误，请检查网络！');
            } else {
                Message.error('未知错误，请稍后重试！');
            }
        }
        return Promise.reject(error);
    }
);


/**
 * 封装GET请求
 * @param {string} baseURL 基础路径
 * @param {string} url 请求地址
 * @param {object} params 查询参数
 * @param {object} config 额外配置
 */
export function get(baseURL, url, params = {}, config = {}) {
    return service({
        ...config,
        baseURL,
        url,
        method: 'get',
        params
    });
}

/**
 * 封装POST请求
 * @param {string} baseURL 基础路径
 * @param {string} url 请求地址
 * @param {object} data 请求体数据
 * @param {object} config 额外配置
 */
export function post(baseURL, url, data = {}, config = {}) {
    return service({
        ...config,
        baseURL,
        url,
        method: 'post',
        data
    });
}

/**
 * 封装PUT请求
 * @param {string} baseURL 基础路径
 * @param {string} url 请求地址
 * @param {object} data 请求体数据
 * @param {object} config 额外配置
 */
export function put(baseURL, url, data = {}, config = {}) {
    return service({
        ...config,
        baseURL,
        url,
        method: 'put',
        data
    });
}

/**
 * 封装DELETE请求
 * @param {string} baseURL 基础路径
 * @param {string} url 请求地址
 * @param {object} config 额外配置
 */
export function del(baseURL, url, config = {}) {
    return service({
        ...config,
        baseURL,
        url,
        method: 'delete'
    });
}

export function upload(baseURL, url, formData, config = {}) {
    // 设置默认的请求头
    const headers = {
        'Content-Type': 'multipart/form-data',
        ...config.headers
    };

    return service({
        ...config,
        baseURL,
        url,
        method: 'post',
        data: formData,
        headers
    });
}

// 导出原始实例以便自定义请求
export default service;